import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, Minus, Plus, Calendar, User, MoreVertical, Trash2, CheckCircle2, Sparkles, Edit2 } from 'lucide-react';
import { useKPIs, KPI } from '../contexts/KPIContext';
import { KPITimelineChart } from './KPITimelineChart';
export function KPIsTab() {
  const {
    kpis,
    removeKPI,
    addKPI,
    updateKPI
  } = useKPIs();
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'leading' | 'lagging'>('all');
  const [editingKPI, setEditingKPI] = useState<string | null>(null);
  // Form state
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState('');
  const [type, setType] = useState<'leading' | 'lagging'>('leading');
  const [baseline, setBaseline] = useState('');
  const [target, setTarget] = useState('');
  const [unit, setUnit] = useState('%');
  const [owner, setOwner] = useState('');
  const [date, setDate] = useState('');
  const filteredKPIs = kpis.filter(k => filterType === 'all' || k.type === filterType);
  const stats = {
    on_track: filteredKPIs.filter(k => k.status === 'on_track').length,
    at_risk: filteredKPIs.filter(k => k.status === 'at_risk').length,
    behind: filteredKPIs.filter(k => k.status === 'behind').length,
    achieved: filteredKPIs.filter(k => k.status === 'achieved').length
  };
  const getStatusConfig = (status: KPI['status']) => {
    switch (status) {
      case 'on_track':
        return {
          label: 'On Track',
          color: 'text-emerald-700',
          bg: 'bg-emerald-100',
          border: 'border-emerald-200',
          icon: TrendingUp
        };
      case 'at_risk':
        return {
          label: 'At Risk',
          color: 'text-amber-700',
          bg: 'bg-amber-100',
          border: 'border-amber-200',
          icon: Minus
        };
      case 'behind':
        return {
          label: 'Behind',
          color: 'text-red-700',
          bg: 'bg-red-100',
          border: 'border-red-200',
          icon: TrendingDown
        };
      case 'achieved':
        return {
          label: 'Achieved',
          color: 'text-blue-700',
          bg: 'bg-blue-100',
          border: 'border-blue-200',
          icon: CheckCircle2
        };
    }
  };
  const handleEditClick = (kpi: KPI) => {
    setTitle(kpi.title);
    setMetric(kpi.metric);
    setType(kpi.type);
    setBaseline(kpi.baselineValue.toString());
    setTarget(kpi.targetValue.toString());
    setUnit(kpi.unit);
    setOwner(kpi.owner);
    setDate(kpi.targetDate);
    setEditingKPI(kpi.id);
    setShowAddForm(true);
    setActiveMenu(null);
  };
  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingKPI(null);
    setTitle('');
    setMetric('');
    setType('leading');
    setBaseline('');
    setTarget('');
    setUnit('%');
    setOwner('');
    setDate('');
  };
  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !metric || !baseline || !target || !owner || !date) return;
    if (editingKPI) {
      updateKPI(editingKPI, {
        title,
        metric,
        type,
        baselineValue: Number(baseline),
        targetValue: Number(target),
        unit,
        targetDate: date,
        owner
      });
    } else {
      addKPI({
        title,
        metric,
        type,
        baselineValue: Number(baseline),
        currentValue: Number(baseline),
        targetValue: Number(target),
        unit,
        targetDate: date,
        status: 'on_track',
        owner
      });
    }
    handleCancelForm();
  };
  // Simple SVG Sparkline
  const Sparkline = ({
    data,
    color
  }: {
    data: number[];
    color: string;
  }) => {
    if (data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const width = 60;
    const height = 20;
    const points = data.map((val, i) => {
      const x = i / (data.length - 1) * width;
      const y = height - (val - min) / range * height;
      return `${x},${y}`;
    }).join(' ');
    return <svg width={width} height={height} className="overflow-visible" data-id="element-852">
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-853" />
      </svg>;
  };
  return <div className="flex flex-col h-full" data-id="element-854">
      <KPITimelineChart data-id="element-855" />

      {/* Header & Stats */}
      <div className="mb-4 space-y-3" data-id="element-856">
        <div className="flex items-center justify-between" data-id="element-857">
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg" data-id="element-858">
            <button onClick={() => setFilterType('all')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterType === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-859">
              All KPIs
            </button>
            <button onClick={() => setFilterType('leading')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterType === 'leading' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-860">
              Leading (Proactive)
            </button>
            <button onClick={() => setFilterType('lagging')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterType === 'lagging' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-861">
              Lagging (Outcomes)
            </button>
          </div>
          <button onClick={() => {
          handleCancelForm();
          setShowAddForm(true);
        }} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-sm font-medium transition-colors" data-id="element-862">
            <Plus size={16} data-id="element-863" />
            Add KPI
          </button>
        </div>

        <div className="flex flex-wrap gap-2" data-id="element-864">
          {stats.on_track > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-full text-xs font-medium text-emerald-700" data-id="element-865">
              <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-866" />
              {stats.on_track} On Track
            </div>}
          {stats.at_risk > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-100 rounded-full text-xs font-medium text-amber-700" data-id="element-867">
              <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-868" />
              {stats.at_risk} At Risk
            </div>}
          {stats.behind > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-100 rounded-full text-xs font-medium text-red-700" data-id="element-869">
              <span className="w-2 h-2 rounded-full bg-red-500" data-id="element-870" />
              {stats.behind} Behind
            </div>}
          {stats.achieved > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-100 rounded-full text-xs font-medium text-blue-700" data-id="element-871">
              <span className="w-2 h-2 rounded-full bg-blue-500" data-id="element-872" />
              {stats.achieved} Achieved
            </div>}
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence data-id="element-873">
        {showAddForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-4" data-id="element-874">
            <form onSubmit={handleAddSubmit} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4" data-id="element-875">
              <div className="flex items-center justify-between mb-2" data-id="element-876">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-877">
                  <Target size={16} className="text-indigo-600" data-id="element-878" />
                  {editingKPI ? 'Edit KPI' : 'New KPI'}
                </h3>
                <button type="button" onClick={handleCancelForm} className="text-gray-400 hover:text-gray-600 text-sm" data-id="element-879">
                  Cancel
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3" data-id="element-880">
                <div className="col-span-2" data-id="element-881">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-882">
                    KPI Title
                  </label>
                  <input type="text" required value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Reduce Fall Hazards" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-883" />
                </div>
                <div data-id="element-884">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-885">
                    Metric Name
                  </label>
                  <input type="text" required value={metric} onChange={e => setMetric(e.target.value)} placeholder="e.g., Fall Incidents" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-886" />
                </div>
                <div data-id="element-887">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-888">
                    Type
                  </label>
                  <select value={type} onChange={e => setType(e.target.value as 'leading' | 'lagging')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-889">
                    <option value="leading" data-id="element-890">Leading (Proactive)</option>
                    <option value="lagging" data-id="element-891">Lagging (Outcome)</option>
                  </select>
                </div>
                <div data-id="element-892">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-893">
                    Unit
                  </label>
                  <select value={unit} onChange={e => setUnit(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-894">
                    <option value="%" data-id="element-895">%</option>
                    <option value="hrs" data-id="element-896">Hours</option>
                    <option value="count" data-id="element-897">Count</option>
                    <option value="pts" data-id="element-898">Points</option>
                    <option value="rate" data-id="element-899">Rate</option>
                  </select>
                </div>
                <div data-id="element-900">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-901">
                    Baseline Value
                  </label>
                  <input type="number" step="any" required value={baseline} onChange={e => setBaseline(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-902" />
                </div>
                <div data-id="element-903">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-904">
                    Target Value
                  </label>
                  <input type="number" step="any" required value={target} onChange={e => setTarget(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-905" />
                </div>
                <div data-id="element-906">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-907">
                    Owner
                  </label>
                  <input type="text" required value={owner} onChange={e => setOwner(e.target.value)} placeholder="e.g., Safety Team" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-908" />
                </div>
                <div data-id="element-909">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-910">
                    Target Date
                  </label>
                  <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" data-id="element-911" />
                </div>
              </div>

              <div className="pt-2" data-id="element-912">
                <button type="submit" className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-913">
                  {editingKPI ? 'Save Changes' : 'Create KPI'}
                </button>
              </div>
            </form>
          </motion.div>}
      </AnimatePresence>

      {/* KPI List */}
      <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3 pb-4" data-id="element-914">
        {filteredKPIs.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-915">
            <Target size={32} className="mx-auto text-gray-400 mb-3" data-id="element-916" />
            <h3 className="text-sm font-medium text-gray-900" data-id="element-917">
              No KPIs tracked
            </h3>
            <p className="text-xs text-gray-500 mt-1" data-id="element-918">
              Set goals to track your safety improvements.
            </p>
          </div> : filteredKPIs.map((kpi, index) => {
        const statusConf = getStatusConfig(kpi.status);
        const StatusIcon = statusConf.icon;
        // Calculate progress percentage for the bar
        const totalRange = Math.abs(kpi.targetValue - kpi.baselineValue) || 1;
        const currentProgress = Math.abs(kpi.currentValue - kpi.baselineValue);
        const progressPercent = Math.min(100, Math.max(0, currentProgress / totalRange * 100));
        // Sparkline color
        const sparkColor = kpi.status === 'on_track' || kpi.status === 'achieved' ? '#10b981' // emerald-500
        : kpi.status === 'at_risk' ? '#f59e0b' // amber-500
        : '#ef4444'; // red-500
        return <motion.div key={kpi.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition-colors relative group" data-id="element-919">
                {/* Header */}
                <div className="flex items-start justify-between mb-3" data-id="element-920">
                  <div className="pr-8" data-id="element-921">
                    <div className="flex items-center gap-2 mb-1" data-id="element-922">
                      <h3 className="font-bold text-gray-900" data-id="element-923">{kpi.title}</h3>
                      {kpi.linkedInsightId && <span className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[9px] font-bold uppercase tracking-wider rounded border border-indigo-100" data-id="element-924">
                          <Sparkles size={10} data-id="element-925" />
                          From Insight
                        </span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-926">
                      <span className="flex items-center gap-1" data-id="element-927">
                        <User size={12} data-id="element-928" />
                        {kpi.owner}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-929">
                        <Calendar size={12} data-id="element-930" />
                        Due {new Date(kpi.targetDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-md border ${statusConf.bg} ${statusConf.color} ${statusConf.border}`} data-id="element-931">
                    <StatusIcon size={14} data-id="element-932" />
                    <span className="text-xs font-semibold" data-id="element-933">
                      {statusConf.label}
                    </span>
                  </div>
                </div>

                {/* Progress Section */}
                <div className="mt-4" data-id="element-934">
                  <div className="flex items-end justify-between mb-1.5" data-id="element-935">
                    <div data-id="element-936">
                      <span className="text-xs font-medium text-gray-500" data-id="element-937">
                        {kpi.metric}
                      </span>
                      <div className="flex items-baseline gap-1 mt-0.5" data-id="element-938">
                        <span className="text-xl font-bold text-gray-900" data-id="element-939">
                          {kpi.currentValue}
                          {kpi.unit}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-940">
                          / {kpi.targetValue}
                          {kpi.unit} target
                        </span>
                      </div>
                    </div>

                    {/* Sparkline */}
                    <div className="flex flex-col items-end" data-id="element-941">
                      <span className="text-[10px] text-gray-400 mb-1" data-id="element-942">
                        Trend
                      </span>
                      <Sparkline data={kpi.history.map(h => h.value)} color={sparkColor} data-id="element-943" />
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mt-2" data-id="element-944">
                    <div className={`absolute top-0 bottom-0 left-0 rounded-full transition-all duration-500 ${kpi.status === 'behind' ? 'bg-red-500' : kpi.status === 'at_risk' ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{
                width: `${progressPercent}%`
              }} data-id="element-945" />
                  </div>
                  <div className="flex justify-between mt-1" data-id="element-946">
                    <span className="text-[10px] text-gray-400" data-id="element-947">
                      Baseline: {kpi.baselineValue}
                      {kpi.unit}
                    </span>
                    <span className="text-[10px] text-gray-400" data-id="element-948">
                      Target: {kpi.targetValue}
                      {kpi.unit}
                    </span>
                  </div>
                </div>

                {/* Context Menu */}
                <div className="absolute top-3 right-3" data-id="element-949">
                  <button onClick={() => setActiveMenu(activeMenu === kpi.id ? null : kpi.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-950">
                    <MoreVertical size={16} data-id="element-951" />
                  </button>

                  <AnimatePresence data-id="element-952">
                    {activeMenu === kpi.id && <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 shadow-lg rounded-lg py-1 z-10" data-id="element-953">
                        <button onClick={() => handleEditClick(kpi)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left" data-id="element-954">
                          <Edit2 size={14} data-id="element-955" />
                          Edit KPI
                        </button>
                        <button onClick={() => {
                  removeKPI(kpi.id);
                  setActiveMenu(null);
                }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 text-left" data-id="element-956">
                          <Trash2 size={14} data-id="element-957" />
                          Delete KPI
                        </button>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.div>;
      })}
      </div>
    </div>;
}